import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, Smartphone, Monitor, Trophy, Users, Sparkles, Crown,
  Send, Check, X, Camera, Upload, Inbox, RefreshCw, MessageSquare, History, Home,
  Flag, ShieldAlert,
} from "lucide-react";

/**
 * Dream Team mode (iter 69).
 */
export default function DreamTeamV2() {
  const { t } = useT();
  const { pool: rawPool } = useParams();
  const pool = (rawPool || "mobile").toLowerCase();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("home");
  const [state, setState] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/dt/state?pool=${pool}`);
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [pool]);

  useEffect(() => { load(); }, [load]);

  // Heartbeat every 30s — drives "online" status in the lobby.
  useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      try { await api.post("/dt/heartbeat", { pool }); } catch { /* noop */ }
    };
    ping();
    const id = setInterval(() => { if (!cancelled) ping(); }, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [pool]);

  // Auto-handle incoming invites (toast + accept/decline).
  useEffect(() => {
    if (!state?.incoming_invites?.length) return;
    state.incoming_invites.forEach((inv) => {
      toast.message(
        t("dt.invite.notify", { name: inv.from?.username || t("dt.invite.aPlayer"), n: inv.game_num }),
        {
          duration: 25000,
          action: {
            label: t("dt.invite.accept"),
            onClick: async () => {
              try {
                const { data } = await api.post(`/dt/invite/${inv.id}/accept`, {});
                toast.success(t("dt.invite.started"));
                navigate(`/v2/dream-team/${pool}?match=${data.match.id}`);
                load();
              } catch (e) { toast.error(formatApiError(e)); }
            },
          },
          cancel: {
            label: t("dt.invite.decline"),
            onClick: async () => {
              try { await api.post(`/dt/invite/${inv.id}/decline`, {}); load(); }
              catch (e) { toast.error(formatApiError(e)); }
            },
          },
        }
      );
    });
  }, [state?.incoming_invites?.length]);  // eslint-disable-line

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#04140A]">
        <Loader2 className="w-6 h-6 animate-spin text-[#FFD700]" />
      </div>
    );
  }

  const PoolIcon = pool === "console" ? Monitor : Smartphone;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#04140A] via-[#06180C] to-[#05140A] text-white pb-32" data-testid="v2-dt-root">
      <div className="px-4 pt-5 pb-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981] text-[#020617] text-[9px] font-extrabold uppercase tracking-[0.3em]">
            <Sparkles className="w-3 h-3" /> {t("dt.headerChip")}
          </span>
          <button
            type="button"
            onClick={() => navigate("/v2/wc2026/dream-team")}
            data-testid="v2-dt-back"
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/70 hover:text-white"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> {t("dt.back")}
          </button>
        </div>
        <div className="relative overflow-hidden rounded-3xl bg-[#020617] border border-[#10B981]/30">
          <div aria-hidden className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-emerald-500/20 blur-[140px]" />
            <div className="absolute -bottom-24 right-0 w-72 h-72 rounded-full bg-[#FFD700]/15 blur-[140px]" />
          </div>
          <div className="relative px-5 py-6 sm:py-8 flex items-center gap-4">
            <img src="/wc/wc2026-dark.png" alt="" className="h-16 sm:h-20 w-auto object-contain" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#10B981]">{t("dt.kicker")}</div>
              <div className="font-heading text-xl sm:text-2xl uppercase tracking-wider leading-none mt-1">{t("dt.title")}</div>
              <div className="text-[11px] text-white/60 mt-1">{t("dt.sub")}</div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-white/70">
          <PoolIcon className="w-3.5 h-3.5" />
          <span>{pool}</span>
          <span className="text-white/40">·</span>
          <span className="text-[#FFD700]">{state.cycle.cycle_id}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 px-4 flex gap-1 border-b border-white/10 overflow-x-auto">
        {[
          { id: "home",        label: t("dt.tabs.home"),        Icon: Home },
          { id: "lobby",       label: t("dt.tabs.lobby"),       Icon: Users },
          { id: "match",       label: t("dt.tabs.match"),       Icon: MessageSquare },
          { id: "leaderboard", label: t("dt.tabs.leaderboard"), Icon: Trophy },
          { id: "history",     label: t("dt.tabs.history"),     Icon: History },
        ].map(({ id, label, Icon }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              data-testid={`v2-dt-tab-${id}`}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-mono uppercase tracking-widest border-b-2 transition-colors ${
                active ? "border-[#FFD700] text-[#FFD700]" : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              <Icon className="w-3 h-3" /> {label}
            </button>
          );
        })}
      </div>

      <div className="px-4 mt-4">
        {tab === "home" && <HomeView state={state} />}
        {tab === "lobby" && <LobbyView pool={pool} state={state} onChanged={load} />}
        {tab === "match" && <MatchView pool={pool} state={state} onChanged={load} userId={user?.id} />}
        {tab === "leaderboard" && <LeaderboardView pool={pool} userId={user?.id} />}
        {tab === "history" && <HistoryView pool={pool} userId={user?.id} />}
      </div>
    </div>
  );
}

/* ===================== HOME (cycle splits + game picker) ===================== */
const CONTINENTS = [
  { key: "europe",   i18n: "dt.continent.europe",   flag: "🇪🇺", color: "#3B82F6" },
  { key: "africa",   i18n: "dt.continent.africa",   flag: "🌍", color: "#F59E0B" },
  { key: "asia",     i18n: "dt.continent.asia",     flag: "🌏", color: "#EF4444" },
  { key: "americas", i18n: "dt.continent.americas", flag: "🌎", color: "#10B981" },
];

const POSITION_META = {
  defenders:   { i18n: "dt.pos.def", icon: "🛡️" },
  midfielders: { i18n: "dt.pos.mid", icon: "🎯" },
  forwards:    { i18n: "dt.pos.fwd", icon: "⚡" },
};

function HomeView({ state }) {
  const { t } = useT();
  const cur = state.current_game;
  const total = state.cycle?.total_games || 20;
  const split = state.current_split;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-[#FFD700]/15 via-[#0a2510] to-[#04140A] border border-[#FFD700]/30 p-4" data-testid="v2-dt-cycle-card">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFD700]">{t("dt.week", { id: state.cycle.cycle_id })}</div>
        <div className="text-base font-extrabold text-white mt-0.5">
          {t("dt.gameOf")} <span className="tabular-nums">{cur}</span> <span className="text-white/40 text-sm font-mono">/ {total}</span>
        </div>
        <div className="text-[11px] text-white/70 mt-0.5">
          {t("dt.gameUnlock")}
        </div>
      </div>
      {split ? (
        <SplitReveal split={split} />
      ) : (
        <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-6 text-center text-sm text-emerald-200" data-testid="v2-dt-cycle-done">
          🏁 {t("dt.cycleDone", { n: total })}
        </div>
      )}
    </div>
  );
}

function SplitReveal({ split }) {
  const { t } = useT();
  const totalSize = CONTINENTS.reduce((n, c) => n + (split[c.key] || 0), 0);
  return (
    <div className="rounded-3xl bg-[#0a2510] border border-[#10B981]/30 overflow-hidden" data-testid={`v2-dt-split-${split.game_num}`}>
      <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-[#10B981] border-b border-[#10B981]/20">
        {t("dt.split.banner", { n: split.game_num, total: totalSize })}
      </div>
      <div className="grid grid-cols-1 gap-2 p-3" key={split.game_num}>
        {CONTINENTS.map((c, i) => {
          const count = split[c.key] || 0;
          const positions = (split.positions || {})[c.key] || {};
          return (
            <div
              key={c.key}
              data-testid={`v2-dt-split-tile-${split.game_num}-${c.key}`}
              style={{
                animation: `dtReveal 600ms cubic-bezier(0.16,1,0.3,1) both`,
                animationDelay: `${i * 90}ms`,
                borderColor: `${c.color}55`,
                background: `linear-gradient(135deg, ${c.color}22, transparent 70%)`,
              }}
              className="relative overflow-hidden rounded-2xl border p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="text-2xl">{c.flag}</div>
                  <div>
                    <div className="text-[12px] font-extrabold text-white" style={{ color: c.color }}>
                      {t(c.i18n)}
                    </div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">
                      {t(count === 1 ? "dt.split.player" : "dt.split.players", { n: count })}
                    </div>
                  </div>
                </div>
                <div className="text-2xl font-extrabold tabular-nums" style={{ color: c.color }}>
                  {count}
                </div>
              </div>
              {Object.keys(positions).length > 0 && (
                <div className="grid grid-cols-3 gap-1.5" data-testid={`v2-dt-split-positions-${split.game_num}-${c.key}`}>
                  {Object.entries(POSITION_META).map(([pkey, pmeta]) => (
                    <div
                      key={pkey}
                      data-testid={`v2-dt-split-pos-${split.game_num}-${c.key}-${pkey}`}
                      className="rounded-lg bg-black/30 px-2 py-1.5 text-center"
                    >
                      <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">{pmeta.icon} {t(pmeta.i18n)}</div>
                      <div className="text-base font-extrabold tabular-nums text-white">
                        {positions[pkey] ?? 0}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="px-4 pb-3 text-[10px] font-mono uppercase tracking-widest text-white/40 text-center">
        {t("dt.split.total", { n: totalSize })}
      </div>
      <style>{`@keyframes dtReveal{from{opacity:0;transform:translateY(16px) scale(0.95);}to{opacity:1;transform:translateY(0) scale(1);}}`}</style>
    </div>
  );
}

/* ===================== LOBBY ===================== */
function LobbyView({ pool, state, onChanged }) {
  const { t } = useT();
  const [lobby, setLobby] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const myGame = state.current_game || 1;
  const me = state.me;
  const fetchLobby = useCallback(async () => {
    try {
      const { data } = await api.get(`/dt/lobby?pool=${pool}&game_num=${myGame}`);
      setLobby(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [pool, myGame]);
  useEffect(() => {
    fetchLobby();
    const id = setInterval(fetchLobby, 8000);
    return () => clearInterval(id);
  }, [fetchLobby]);
  const invite = async (uid) => {
    setPendingId(uid);
    try {
      await api.post("/dt/invite", { pool, to_user_id: uid });
      toast.success(t("dt.lobby.inviteToast", { n: myGame }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTimeout(() => setPendingId(null), 5000); }
  };
  if (!lobby) {
    return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FFD700]" /></div>;
  }
  const label = lobby.total_online === 1 ? t("dt.lobby.player") : t("dt.lobby.players");
  return (
    <div className="space-y-3" data-testid="v2-dt-lobby">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC]">
          {t("dt.lobby.online", { n: lobby.total_online, label })}
        </div>
        <button type="button" onClick={fetchLobby} className="text-[#FFD700]/70 hover:text-[#FFD700]" data-testid="v2-dt-lobby-refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      <div className="rounded-2xl bg-[#FFD700]/5 border border-[#FFD700]/30 p-2.5 text-[11px] text-[#FFD700]" data-testid="v2-dt-game-banner">
        {t("dt.lobby.gameBanner", { n: myGame })}
      </div>
      {!me && (
        <div className="rounded-2xl bg-[#10B981]/10 border border-[#10B981]/30 p-3 text-[12px] text-[#10B981]">
          {t("dt.lobby.firstInvite")}
        </div>
      )}
      {lobby.online.length === 0 ? (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-center text-sm text-white/60" data-testid="v2-dt-lobby-empty">
          <Inbox className="w-6 h-6 mx-auto mb-2 opacity-50" />
          {t("dt.lobby.empty")}
        </div>
      ) : (
        <div className="space-y-2">
          {lobby.online.map((p) => {
            const sameGame = (p.current_game_num || 1) === myGame;
            return (
              <div
                key={p.user_id}
                data-testid={`v2-dt-lobby-row-${p.user_id}`}
                className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-2.5 hover:border-[#FFD700]/40 transition-colors"
              >
                <Avatar src={p.avatar_url} name={p.username} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-bold text-white truncate">@{p.username}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span
                      data-testid={`v2-dt-lobby-game-pill-${p.user_id}`}
                      className={`text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${
                        sameGame
                          ? "border-emerald-400/60 text-emerald-300 bg-emerald-500/10"
                          : "border-white/15 text-white/50"
                      }`}
                    >
                      {t("dt.gameOf")} {p.current_game_num || 1}
                    </span>
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">
                    {t("dt.lobby.rankLine", { rank: p.rank ?? "—", pts: p.points, gd: p.gd >= 0 ? `+${p.gd}` : p.gd })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => invite(p.user_id)}
                  disabled={pendingId === p.user_id}
                  data-testid={`v2-dt-invite-btn-${p.user_id}`}
                  className="shrink-0 rounded-full bg-[#FFD700] text-[#04140A] font-bold text-[10px] uppercase tracking-widest px-3 py-1.5 disabled:opacity-50"
                >
                  {pendingId === p.user_id ? t("dt.lobby.inviteSent") : t("dt.lobby.invite")}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Avatar({ src, name }) {
  if (src) {
    return <img src={src} alt={name} className="w-9 h-9 rounded-full object-cover ring-1 ring-white/10 shrink-0" />;
  }
  const initial = (name || "?")[0].toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FFD700] to-[#10B981] flex items-center justify-center font-bold text-[#04140A] text-sm shrink-0">
      {initial}
    </div>
  );
}

/* ===================== MATCH ===================== */
function MatchView({ pool, state, onChanged, userId }) {
  const { t } = useT();
  const m = state.active_match;
  if (!m) {
    return (
      <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-center text-sm text-white/70" data-testid="v2-dt-no-match">
        <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-50" />
        {t("dt.match.empty")}
      </div>
    );
  }
  return <ActiveMatch matchId={m.id} userId={userId} onChanged={onChanged} />;
}

function ActiveMatch({ matchId, userId, onChanged }) {
  const { t } = useT();
  const [match, setMatch] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const chatRef = useRef(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/dt/matches/${matchId}`);
      setMatch(data);
    } catch { /* noop */ }
  }, [matchId]);
  useEffect(() => {
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [load]);
  useEffect(() => {
    if (match && chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [match?.chat?.length]);  // eslint-disable-line
  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/dt/matches/${matchId}/chat`, { text: text.trim() });
      setText("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };
  if (!match) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FFD700]" /></div>;
  const iAmHome = match.home_user === userId;
  const opp = iAmHome ? match.away : match.home;
  const mySplit = iAmHome ? match.home_split : match.away_split;
  const oppSplit = iAmHome ? match.away_split : match.home_split;
  const myGameNum = iAmHome ? match.home_game_num : match.away_game_num;
  const oppGameNum = iAmHome ? match.away_game_num : match.home_game_num;
  const subs = match.subs || {};
  const mySub = subs[userId];
  return (
    <div className="space-y-3" data-testid={`v2-dt-active-match-${match.id}`}>
      <div className="rounded-3xl bg-gradient-to-br from-[#0a2510] to-[#04140A] border border-[#FFD700]/30 overflow-hidden">
        <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-[#FFD700] border-b border-[#FFD700]/20 flex items-center justify-between">
          <span>{t("dt.match.gameStatus", { n: match.game_num, status: match.status.replace(/_/g, " ") })}</span>
          {match.status === "completed" && <span className="text-emerald-300">{t("dt.match.confirmed")}</span>}
          {match.status === "disputed" && <span className="text-rose-300">{t("dt.match.disputed")}</span>}
        </div>
        <div className="grid grid-cols-3 items-center px-4 py-4">
          <div className="text-center" data-testid="v2-dt-match-home-side">
            <Avatar src={match.home?.avatar_url} name={match.home?.username} />
            <div className="text-xs font-bold mt-1 truncate">@{match.home?.username}</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">{t("dt.match.homeLabel", { n: match.home_game_num || match.game_num })}</div>
          </div>
          <div className="text-center">
            {match.status === "completed" || match.status === "disputed" ? (
              <div className="text-3xl font-extrabold tabular-nums text-white">
                {match.home_score} <span className="text-white/30">–</span> {match.away_score}
              </div>
            ) : (
              <div className="text-3xl font-bold text-white/30">vs</div>
            )}
          </div>
          <div className="text-center" data-testid="v2-dt-match-away-side">
            <Avatar src={match.away?.avatar_url} name={match.away?.username} />
            <div className="text-xs font-bold mt-1 truncate">@{match.away?.username}</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">{t("dt.match.awayLabel", { n: match.away_game_num || match.game_num })}</div>
          </div>
        </div>
      </div>

      {(mySplit || oppSplit) && (
        <div className="rounded-2xl bg-black/30 border border-white/10 overflow-hidden" data-testid="v2-dt-match-conditions">
          <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC] border-b border-white/10 flex items-center justify-between">
            <span>{t("dt.match.condTitle")}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 p-2">
            <MiniSplitCard
              title={t("dt.match.condYours")}
              gameNum={myGameNum}
              split={mySplit}
              accent="#FFD700"
              testIdPrefix="v2-dt-cond-mine"
            />
            <MiniSplitCard
              title={t("dt.match.condOpp", { name: opp?.username || t("dt.opp") })}
              gameNum={oppGameNum}
              split={oppSplit}
              accent="#EF4444"
              testIdPrefix="v2-dt-cond-opp"
            />
          </div>
          <div className="px-3 py-2 border-t border-white/10 flex items-center justify-between gap-2">
            <div className="text-[10px] text-white/50 leading-tight">
              {t("dt.match.spotMismatch")}
            </div>
            <button
              type="button"
              onClick={() => setShowReport(true)}
              data-testid="v2-dt-report-open"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/50 text-rose-200 text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 transition-colors"
            >
              <Flag className="w-3 h-3" />
              {match.my_report ? t("dt.match.updateReport") : t("dt.match.reportSquad")}
            </button>
          </div>
          {match.my_report && (
            <div className="px-3 pb-2 -mt-1" data-testid="v2-dt-report-existing">
              <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-2 py-1.5 text-[10px] text-rose-200 inline-flex items-center gap-1.5">
                <ShieldAlert className="w-3 h-3" />
                {t("dt.match.reportedStatus", { status: match.my_report.status || "open" })}
              </div>
            </div>
          )}
        </div>
      )}

      {match.status === "in_progress" && !mySub && (
        <SubmitForm match={match} iAmHome={iAmHome} onSubmitted={() => { load(); onChanged?.(); }} />
      )}
      {match.status === "in_progress" && mySub && (
        <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-3 text-[12px] text-amber-200 text-center" data-testid="v2-dt-waiting-other">
          {t("dt.match.waiting", { h: mySub.home_score, a: mySub.away_score })}
        </div>
      )}
      {match.status === "disputed" && (
        <div className="rounded-2xl bg-rose-500/10 border border-rose-500/30 p-3 text-[12px] text-rose-200 text-center">
          {t("dt.match.disputeBody")}
        </div>
      )}
      {match.status === "completed" && (
        <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-3 text-[12px] text-emerald-200 text-center" data-testid="v2-dt-match-final">
          {t("dt.match.finalLine", { a: iAmHome ? match.points_home : match.points_away, b: iAmHome ? match.points_away : match.points_home })}
        </div>
      )}

      {/* Chat */}
      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid="v2-dt-chat">
        <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-white/50 border-b border-white/10">
          {t("dt.match.chatTitle")}
        </div>
        <div ref={chatRef} className="h-64 overflow-y-auto p-3 space-y-2 bg-black/20">
          {(match.chat || []).length === 0 ? (
            <div className="text-center text-xs text-white/30 mt-12">{t("dt.match.chatEmpty")}</div>
          ) : (
            match.chat.map((c) => (
              <ChatBubble key={c.id} msg={c} mine={c.user_id === userId} />
            ))
          )}
        </div>
        <div className="flex items-center gap-2 p-2 border-t border-white/10">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={t("dt.match.chatPlaceholder")}
            maxLength={1000}
            data-testid="v2-dt-chat-input"
            style={{ fontSize: "16px" }}
            className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#FFD700]"
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || !text.trim()}
            data-testid="v2-dt-chat-send"
            className="rounded-lg bg-[#FFD700] text-[#04140A] font-bold p-2 disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {showReport && (
        <ReportSquadModal
          match={match}
          opp={opp}
          alreadyReported={!!match.my_report}
          onClose={() => setShowReport(false)}
          onDone={() => { setShowReport(false); load(); }}
        />
      )}
    </div>
  );
}

function MiniSplitCard({ title, gameNum, split, accent = "#FFD700", testIdPrefix }) {
  const { t } = useT();
  if (!split) {
    return (
      <div
        data-testid={`${testIdPrefix}-empty`}
        className="rounded-xl border border-white/10 bg-white/5 p-3 text-center text-[10px] text-white/50"
      >
        {t("dt.mini.condPending")}
      </div>
    );
  }
  const total = CONTINENTS.reduce((n, c) => n + (split[c.key] || 0), 0);
  return (
    <div
      data-testid={testIdPrefix}
      className="rounded-xl border bg-black/30 p-2"
      style={{ borderColor: `${accent}55` }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[9px] font-mono uppercase tracking-widest truncate" style={{ color: accent }}>
          {title}
        </div>
        <div
          className="text-[8px] font-mono uppercase tracking-widest rounded-full px-1.5 py-0.5 border"
          style={{ color: accent, borderColor: `${accent}66` }}
          data-testid={`${testIdPrefix}-game`}
        >
          G{gameNum || split.game_num}
        </div>
      </div>
      <div className="space-y-1">
        {CONTINENTS.map((c) => {
          const count = split[c.key] || 0;
          const pos = (split.positions || {})[c.key] || {};
          return (
            <div
              key={c.key}
              data-testid={`${testIdPrefix}-${c.key}`}
              className="flex items-center justify-between rounded-md bg-white/5 px-1.5 py-1"
              style={{ borderLeft: `2px solid ${c.color}88` }}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-[12px]">{c.flag}</span>
                <span className="text-[10px] font-bold truncate" style={{ color: c.color }}>{t(c.i18n)}</span>
              </div>
              <div className="text-[9px] font-mono tabular-nums text-white/80 whitespace-nowrap">
                {count} · <span className="text-white/60">D{pos.defenders ?? 0}/M{pos.midfielders ?? 0}/F{pos.forwards ?? 0}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-center text-[9px] font-mono uppercase tracking-widest text-white/40 mt-1.5">
        {t("dt.mini.totalLine", { n: total })}
      </div>
    </div>
  );
}

function ReportSquadModal({ match, opp, alreadyReported, onClose, onDone }) {
  const { t } = useT();
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const submit = async () => {
    if (reason.trim().length < 5) {
      toast.error(t("dt.report.minReason"));
      return;
    }
    if (!file) {
      toast.error(t("dt.report.needShot"));
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("reason", reason.trim());
      fd.append("screenshot", file);
      // iter 97 — use the axios instance (injects the gml_token bearer +
      // withCredentials). The old raw fetch read localStorage "token" (wrong
      // key) and omitted credentials → 401 in cross-origin deploys.
      await api.post(`/dt/matches/${match.id}/report`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("dt.report.sentToast"));
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e) || t("dt.report.failed"));
    } finally { setBusy(false); }
  };
  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="v2-dt-report-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[#0a1A0F] border border-rose-500/40 rounded-t-3xl sm:rounded-3xl p-4 space-y-3"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-rose-300 flex items-center gap-1.5">
              <Flag className="w-3 h-3" /> {t("dt.report.title")}
            </div>
            <div className="text-base font-extrabold text-white mt-1">
              @{opp?.username || t("dt.opp")}
            </div>
            <div className="text-[11px] text-white/60">
              {t("dt.report.body")}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="v2-dt-report-close"
            className="text-white/40 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {alreadyReported && (
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-2.5 py-1.5 text-[10px] text-amber-200">
            {t("dt.report.already")}
          </div>
        )}

        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-white/50 block mb-1">
            {t("dt.report.issue")}
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("dt.report.placeholder")}
            rows={4}
            maxLength={1000}
            data-testid="v2-dt-report-reason"
            style={{ fontSize: "16px" }}
            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-rose-400"
          />
          <div className="text-right text-[9px] text-white/40 mt-0.5">{reason.length}/1000</div>
        </div>

        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="v2-dt-report-file"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="v2-dt-report-attach"
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-rose-500/15 border border-rose-500/40 text-rose-200 py-2.5 text-[11px] font-mono uppercase tracking-widest hover:bg-rose-500/25 transition-colors"
          >
            <Camera className="w-3.5 h-3.5" />
            {file ? t("dt.report.attached", { name: file.name.slice(0, 24) }) : t("dt.report.attach")}
          </button>
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="v2-dt-report-submit"
          className="w-full rounded-xl bg-gradient-to-r from-rose-500 to-rose-600 text-white font-extrabold text-sm py-3 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          <Flag className="w-4 h-4" /> {t("dt.report.send")}
        </button>
      </div>
    </div>
  );
}

function ChatBubble({ msg, mine }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[75%] rounded-2xl px-3 py-1.5 text-sm ${
        mine ? "bg-[#FFD700] text-[#04140A]" : "bg-white/10 text-white"
      }`}>
        {!mine && <div className="text-[9px] font-mono uppercase tracking-widest opacity-60 mb-0.5">@{msg.username}</div>}
        <div className="break-words">{msg.text}</div>
      </div>
    </div>
  );
}

function SubmitForm({ match, iAmHome, onSubmitted }) {
  const { t } = useT();
  const [home, setHome] = useState(0);
  const [away, setAway] = useState(0);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("home_score", String(Number(home)));
      fd.append("away_score", String(Number(away)));
      if (file) fd.append("screenshot", file);
      await api.post(`/dt/matches/${match.id}/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("dt.submit.toast"));
      onSubmitted?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-3" data-testid="v2-dt-submit-form">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC]">
        {t("dt.submit.title")}
      </div>
      <div className="flex items-center justify-center gap-3">
        <NumInput label={`@${match.home?.username}`} v={home} setV={setHome} testId="v2-dt-submit-home" />
        <div className="text-2xl text-white/40">—</div>
        <NumInput label={`@${match.away?.username}`} v={away} setV={setAway} testId="v2-dt-submit-away" />
      </div>
      <div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="v2-dt-submit-file"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid="v2-dt-submit-attach"
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#7C3AED]/15 border border-[#7C3AED]/40 text-[#A5B4FC] py-2 text-[11px] font-mono uppercase tracking-widest hover:bg-[#7C3AED]/25 transition-colors"
        >
          <Camera className="w-3.5 h-3.5" /> {file ? file.name.slice(0, 20) : t("dt.submit.attachOpt")}
        </button>
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={busy}
        data-testid="v2-dt-submit-go"
        className="w-full rounded-xl bg-gradient-to-r from-[#FFD700] to-[#FFB000] text-[#04140A] font-extrabold text-sm py-3 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        <Upload className="w-4 h-4" />
        {t("dt.submit.cta")}
      </button>
    </div>
  );
}

function NumInput({ label, v, setV, testId }) {
  return (
    <div className="flex-1 max-w-[100px]">
      <label className="text-[9px] font-mono uppercase tracking-widest text-white/40 block truncate">{label}</label>
      <input
        type="number"
        min="0"
        max="99"
        value={v}
        onChange={(e) => setV(e.target.value)}
        data-testid={testId}
        style={{ fontSize: "16px" }}
        className="w-full bg-black/30 border border-white/10 rounded-lg px-2 py-2 text-center font-bold tabular-nums text-white text-xl focus:outline-none focus:border-[#FFD700]"
      />
    </div>
  );
}

/* ===================== LEADERBOARD ===================== */
function LeaderboardView({ pool, userId }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/dt/leaderboard?pool=${pool}`);
        if (!cancelled) setData(data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [pool]);
  if (!data) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FFD700]" /></div>;
  const meInTop = (data.rows || []).some((r) => r.user_id === userId);
  return (
    <div className="space-y-3" data-testid="v2-dt-leaderboard">
      {data.me && !meInTop && (
        <div className="rounded-2xl bg-[#FFD700]/10 border border-[#FFD700]/40 p-3" data-testid="v2-dt-leaderboard-me-card">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700] mb-1">{t("dt.lb.yourRank", { rank: data.me.rank })}</div>
          <LbRow row={data.me} isMe />
        </div>
      )}
      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        <div className="grid grid-cols-[28px_1fr_auto] gap-2 px-3 py-2 text-[9px] font-mono uppercase tracking-widest text-white/40 border-b border-white/10">
          <div>#</div>
          <div>{t("dt.lb.col.player")}</div>
          <div className="text-right tabular-nums">{t("dt.lb.header")}</div>
        </div>
        {data.rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-white/40" data-testid="v2-dt-leaderboard-empty">
            {t("dt.lb.empty")}
          </div>
        ) : (
          data.rows.map((r) => (
            <LbRow key={r.user_id} row={r} isMe={r.user_id === userId} />
          ))
        )}
      </div>
    </div>
  );
}

function LbRow({ row, isMe }) {
  const { t } = useT();
  const medal = row.rank === 1 ? "🥇" : row.rank === 2 ? "🥈" : row.rank === 3 ? "🥉" : null;
  return (
    <div
      data-testid={`v2-dt-leaderboard-row-${row.user_id}`}
      className={`grid grid-cols-[28px_1fr_auto] gap-2 items-center px-3 py-2 border-b border-white/5 last:border-b-0 ${
        isMe ? "bg-[#FFD700]/10 ring-1 ring-inset ring-[#FFD700]/40" : "hover:bg-white/5"
      }`}
    >
      <div className="text-sm font-bold tabular-nums">
        {medal || <span className="text-white/50">{row.rank}</span>}
      </div>
      <div className="flex items-center gap-2 min-w-0">
        <Avatar src={row.avatar_url} name={row.username} />
        <span className={`text-xs font-bold truncate ${isMe ? "text-[#FFD700]" : "text-white"}`}>
          @{row.username}{isMe && ` · ${t("dt.lb.you")}`}
        </span>
      </div>
      <div className="text-[10px] font-mono tabular-nums text-white/70 text-right whitespace-nowrap">
        {row.played} · {row.won}-{row.drawn}-{row.lost} · {row.gd >= 0 ? `+${row.gd}` : row.gd} · <span className="text-white font-bold">{row.points}</span>
      </div>
    </div>
  );
}

/* ===================== HISTORY ===================== */
function HistoryView({ pool, userId }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/dt/matches?pool=${pool}`);
        if (!cancelled) setData(data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [pool]);
  if (!data) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FFD700]" /></div>;
  return (
    <div className="space-y-2" data-testid="v2-dt-history">
      {(data.matches || []).length === 0 ? (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-center text-sm text-white/50" data-testid="v2-dt-history-empty">
          {t("dt.history.empty")}
        </div>
      ) : (
        data.matches.map((m) => {
          const iAmHome = m.home_user === userId;
          const myScore = iAmHome ? m.home_score : m.away_score;
          const oppScore = iAmHome ? m.away_score : m.home_score;
          const opp = iAmHome ? m.away : m.home;
          const tag = m.status === "completed"
            ? (myScore > oppScore ? t("dt.history.win") : myScore < oppScore ? t("dt.history.loss") : t("dt.history.draw"))
            : m.status.toUpperCase();
          const tagColor = m.status === "completed"
            ? (myScore > oppScore ? "text-emerald-400 bg-emerald-500/10"
              : myScore < oppScore ? "text-rose-400 bg-rose-500/10"
              : "text-amber-400 bg-amber-500/10")
            : "text-white/60 bg-white/10";
          return (
            <div
              key={m.id}
              data-testid={`v2-dt-history-row-${m.id}`}
              className="rounded-2xl bg-white/5 border border-white/10 p-3 flex items-center gap-3"
            >
              <Avatar src={opp?.avatar_url} name={opp?.username} />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-white truncate"><span className="text-white/40">vs</span> @{opp?.username}</div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">{t("dt.history.gameNum", { n: m.game_num })}</div>
              </div>
              {m.status === "completed" ? (
                <div className="text-base font-extrabold tabular-nums text-white">
                  {myScore}<span className="text-white/30">–</span>{oppScore}
                </div>
              ) : <span className="text-[9px] font-mono uppercase tracking-widest text-white/50">—</span>}
              <span className={`text-[9px] font-mono uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${tagColor}`}>
                {tag}
              </span>
            </div>
          );
        })
      )}
    </div>
  );
}
